<template>
  <div class="slot-buttons">
    <div
      ref="container"
      class="slot-buttons__container"
      :style="{
        'grid-template-columns': `repeat(${numberOfCols}, 1fr)`,
      }"
    >
      <button
        v-for="(slot, slotIndex) in currentDaySlots"
        v-show="isExpanded || slotIndex < numberOfVisibleSlots - 1 || currentDaySlots.length <= numberOfVisibleSlots"
        :key="slotIndex"
        class="slot-buttons__button b-button pa-1"
        :class="getNamespace(slot).button"
        data-qa="appointment-calendar-button-slot"
        data-google-goal="click_appointment_slot"
        type="button"
        :data-doctor="doctorId"
        @click="handleClickSlot($event, slot)"
      >
        <span
          class="text-subtitle-1"
          :class="getNamespace(slot).text"
        >
          {{ slot.time }}
        </span>
      </button>
      <button
        v-if="hasExpandButton"
        type="button"
        class="slot-buttons__button b-button b-button_light primary--text pa-1"
        data-qa="appointment-calendar-button-expand"
        @click="isExpanded = !isExpanded"
      >
        <span
          class="ui-icon-fz-medium"
          :class="isExpanded ? 'ui-icon-arrow-up' : 'ui-icon-arrow-down'"
        />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { isFeatureFlagEnabledHTML } from 'www/utils'
import { TOWN_ONLINE_GOALS } from 'www/yandexGoals'
import type { SlotGeneral, SlotsGeneral } from 'www/api/types'
import sendYaGoal from 'www/vue_components/common/AppointmentPage/functions/sendYaGoal'

const props = withDefaults(defineProps<{
  currentDaySlots: SlotsGeneral[string]
  selectedSlot?: SlotGeneral
  isCurrentDaySelected: boolean
  doctorId?: number
  isDisabled?: boolean
}>(), {
  isDisabled: false,
  doctorId: 0,
  // @ts-ignore
  selectedSlot: null,
  isCurrentDaySelected: false,
})

const emit = defineEmits(['slot-buttons:click'])

const isMobile = window.MOBILE_VERSION

const DEFAULT_NUMBER_OF_COLS = isMobile ? 4 : 5
const NUMBER_OF_ROWS = 2

const container = ref<HTMLDivElement>()

const isExpanded = ref(false)

const numberOfCols = computed(() => {
  if (isMobile || !container.value) {
    return DEFAULT_NUMBER_OF_COLS
  }

  const containerWidth = container.value.offsetWidth

  if (containerWidth < 500) {
    return DEFAULT_NUMBER_OF_COLS
  }

  return Math.round(containerWidth / 100)
})

const numberOfVisibleSlots = computed(() => NUMBER_OF_ROWS * numberOfCols.value)

const hasExpandButton = computed(() => props.currentDaySlots.length > numberOfVisibleSlots.value)

function handleClickSlot(event: Event, currentSlot: SlotGeneral) {
  if (props.isDisabled) {
    return
  }

  if (isFeatureFlagEnabledHTML({ dataAttr: 'data-is-town-online' })) {
    sendYaGoal(TOWN_ONLINE_GOALS.clickSlot)
  }

  emit('slot-buttons:click', event, currentSlot)
}

function checkSlotIsSelected(slot: SlotGeneral) {
  return props.isCurrentDaySelected && props.selectedSlot?.time === slot.time
}

function getNamespace(slot: SlotGeneral): { button: string, text: string } {
  const result = {
    text: 'bg-gray-0--text',
    button: 'primary',
  }

  if (!slot) {
    return result
  }

  if (checkSlotIsSelected(slot)) {
    result.text = 'primary--text'
    result.button = 'b-button_light b-button_outline bg-gray-0--text'
  }

  if (props.isDisabled) {
    result.text = 'text-subtitle-1 text--text'
    result.button = 'bg-gray-0 cursor-default'
  }

  return result
}
</script>

<style scoped lang="scss">
.slot-buttons {
  &__button {
    transition: none;
    height: 40px;
  }

  &__container {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 10px;
  }
}
</style>
